import React, { useState } from 'react';
import { LogIn, Loader2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../lib/apiBase';

axios.defaults.withCredentials = true;

/**
 * Admin sign-in screen — username + password form.
 * On success the backend sets the httpOnly `session_token` cookie and we
 * forward to the page the user originally tried to reach (or /admin).
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const next = location.state?.from?.pathname || '/admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/auth/login`,
        { username: username.trim(), password },
        { withCredentials: true },
      );
      setUser?.(data);
      navigate(next, { replace: true });
    } catch (err) {
      // FastAPI returns either {detail: "..."} or {detail: [{msg: ...}]}.
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join(' ')
          : (err.message || 'Login failed');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafbfc] px-4">
      <div className="max-w-sm w-full bg-white border border-[#eee] rounded-2xl shadow-sm p-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-[#eaf3fa] text-[#0184ff] mb-3">
            <LogIn className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-semibold text-[#1a4d7a]">SitgesVibes Admin</h1>
          <p className="text-sm text-[#6b7a8c] mt-1">Sign in to manage the catalogue.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-medium text-[#6b7a8c] mb-1">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0184ff] focus:border-transparent text-sm"
              data-testid="admin-login-username"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#6b7a8c] mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0184ff] focus:border-transparent text-sm"
              data-testid="admin-login-password"
            />
          </div>

          {error && (
            <div
              className="text-sm text-[#b1283d] bg-[#fef2f4] border border-[#fbcad1] rounded-lg px-3 py-2"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !username.trim() || !password}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0184ff] hover:bg-[#0070dd] text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-login-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </button>
        </form>

        <p className="mt-5 text-[11px] text-[#9aa5b1] text-center">
          Access is restricted to a single allow-listed account.
        </p>
      </div>
    </div>
  );
}
